// Explicit state machine for one player tile.
//
// Every transition is total (assertNever below makes an unhandled action a
// compile error) and most are unconditional — the four guarded ones exist for
// reasons worth keeping:
//   - a stall reported before playback ever started must not clobber
//     informative "starting…" copy with a bare spinner
//   - a late phase-change event from a run that has since stopped must not
//     re-spin a tile that has already moved on
//   - a stale "device offline" signal must never interrupt live video
export type StartPhase = "requesting" | "connecting" | "buffering_segments" | "attaching_player";

export type PlayerErrorCode =
  | "start_failed"
  | "camera_disabled"
  | "timeout"
  | "stopped_by_server"
  | "lease_lost"
  | "hls_network"
  | "hls_media"
  | "hls_other"
  | "unsupported_browser"
  | "unsupported_codec"
  // Every slot allowed by the site's limit or the global one is taken by a
  // running stream. Not a fault, and not retryable on its own — the wall is
  // full until a limit is raised or something stops.
  | "quota_exhausted";

export type PlayerState =
  | { kind: "idle" }
  // Refused a slot, and waiting for one to free.
  //
  // Deliberately a state rather than an error. Waiting is the ordinary
  // consequence of a full wall, not a fault: nothing has gone wrong, nothing
  // needs an operator, and the tile recovers on its own the moment another
  // one stops. Modelling it as an error rendered a red failure panel with a
  // dead end — the old `slot_limit` code was marked non-retryable and no
  // retry was ever attempted, so a tile that lost the race stayed dead until
  // the page was reloaded.
  | { kind: "queued" }
  | { kind: "starting"; phase: StartPhase }
  | { kind: "playing" }
  | { kind: "buffering" }
  | { kind: "paused" }
  // hls.js's own fast recovery (a few retries inside a short window, see
  // recoveryBudget.ts) has been spent, but the confirmed product decision is
  // to keep trying rather than dead-end: the backend's own RTSP pull retries
  // forever too (see the backend's reconnect loop), so a camera that recovers
  // a few seconds later than this browser's patience should not need a human
  // to notice and click Retry. Deliberately not `error` — this is the same
  // "waiting, not broken" shape as `queued` above, not a fault needing an
  // operator. `attempt` is cosmetic (shown in the copy) and counts
  // RECOVERY_EXHAUSTED dispatches since the tile last actually played.
  | { kind: "reconnecting"; attempt: number }
  | { kind: "error"; code: PlayerErrorCode; detail?: string };

export type PlayerAction =
  | { type: "PLAY_REQUESTED" }
  | { type: "QUEUED" }
  | { type: "PHASE_CHANGED"; phase: StartPhase }
  | { type: "PLAYING" }
  | { type: "STALLED" }
  | { type: "PAUSED" }
  | { type: "STOPPED" }
  | { type: "RECOVERY_EXHAUSTED" }
  | { type: "FAILED"; code: PlayerErrorCode; detail?: string };

export const initialPlayerState: PlayerState = { kind: "idle" };

function assertNever(x: never): never {
  throw new Error(`unhandled player action: ${JSON.stringify(x)}`);
}

export function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case "PLAY_REQUESTED":
      return { kind: "starting", phase: "requesting" };

    case "QUEUED":
      return { kind: "queued" };

    case "PHASE_CHANGED":
      // A late event from a run that has since stopped must not re-spin the
      // tile into "starting" after it has moved to idle/error/playing.
      return state.kind === "starting" ? { kind: "starting", phase: action.phase } : state;

    case "PLAYING":
      return { kind: "playing" };

    case "STALLED":
      // A stall reported before playback ever began would otherwise replace
      // the informative "starting…" copy with a bare buffering spinner.
      return state.kind === "playing" || state.kind === "buffering" ? { kind: "buffering" } : state;

    case "PAUSED":
      return { kind: "paused" };

    case "STOPPED":
      return { kind: "idle" };

    case "RECOVERY_EXHAUSTED":
      return { kind: "reconnecting", attempt: state.kind === "reconnecting" ? state.attempt + 1 : 1 };

    case "FAILED":
      return { kind: "error", code: action.code, detail: action.detail };

    default:
      return assertNever(action);
  }
}

/**
 * Whether this state should still be counted against the viewer's lease.
 *
 * "queued" is false: a waiting tile holds neither a slot nor a lease — that is
 * precisely what it is waiting for.
 */
export function holdsLease(state: PlayerState): boolean {
  return state.kind === "starting" || state.kind === "playing" || state.kind === "buffering" || state.kind === "paused";
}
